import axios from 'axios';
import Cookies from 'js-cookie';
import { DEFAULT_REDIRECT_URL } from 'dashboard/constants/globals';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { getCentralHeaders } from 'dashboard/helpers/centralApiHeaders';

export const hasAuthCookie = () => {
  return !!Cookies.get('cw_d_session_info');
};

const getSSOAccountPath = ({ ssoAccountId, user }) => {
  const { accounts = [], account_id = null } = user || {};
  const ssoAccount = accounts.find(
    account => account.id === Number(ssoAccountId)
  );
  let accountId = null;
  let accountPath = '';
  if (ssoAccount) {
    accountId = Number(ssoAccountId);
    accountPath = `accounts/${accountId}`;
  } else if (accounts.length) {
    // If the account id is not found, redirect to the first account
    accountId = account_id || accounts[0].id;
    accountPath = `accounts/${accountId}`;
  }
  return { accountId, accountPath };
};

// This runs on the login page's own Vuex store (only registers globalConfig,
// no `stripe`/`onboarding` modules — see CLAUDE.md), so it can't reuse
// store/modules/stripe.js's fetchSubscription action and makes its own raw
// call instead. This is the second, independent copy of the entitlement
// check referenced in the plan doc.
const getEntitlement = async accountId => {
  const proxyBase = `/api/v1/accounts/${accountId}/proxy/knowledge`;
  try {
    const { data: response } = await axios.get(
      `${proxyBase}/account/${accountId}/entitlement`,
      { headers: getCentralHeaders() }
    );
    return { status: response.status };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
    // A 404 confirms there's no entitlement row yet. Any other error (e.g.
    // a 5xx) is inconclusive — don't guess here, defer to the dashboard's
    // own subscriptionGuard, which distinguishes "not active" from
    // "couldn't check" and can route to a proper error page.
    return { status: err?.response?.status === 404 ? null : undefined };
  }
};

// Same reasoning as getEntitlement above — a raw call rather than going
// through a Vuex module action, since no store module is registered on
// this page.
//
// Deliberately NOT `GET .../account/{id}/onboarding` (the task-checklist
// endpoint) — confirmed live that it doesn't reliably include a
// `milestones` key at all (it only returns `{version, tasks}` for an
// account that's never had a milestone posted). POST
// .../onboarding/milestone is idempotent — re-posting an already-set key
// returns the original timestamp, not a new one — and its response always
// includes the full current milestones map, so it doubles as the read
// path here. "started" is also the semantically correct key to fire at
// this exact decision point, since we're evaluating onboarding-funnel
// entry.
const getOnboardingMilestones = async accountId => {
  const proxyBase = `/api/v1/accounts/${accountId}/proxy/knowledge`;
  try {
    const { data: response } = await axios.post(
      `${proxyBase}/account/${accountId}/onboarding/milestone`,
      { key: 'started' },
      { headers: getCentralHeaders() }
    );
    return response?.milestones || {};
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
    // Non-critical — if this fails, fall through to the normal dashboard
    // redirect rather than blocking login on a milestones lookup.
    return null;
  }
};

// Redirect to pricing table to check for subscription first
export const getLoginRedirectURL = async ({
  ssoAccountId,
  ssoConversationId,
  user,
}) => {
  const { accountId, accountPath } = getSSOAccountPath({ ssoAccountId, user });
  if (accountPath) {
    const { status } = await getEntitlement(accountId);
    if (status === undefined) {
      return DEFAULT_REDIRECT_URL;
    }
    // A confirmed 404 (no entitlement row at all) doesn't necessarily mean
    // the user needs to pick a plan — a brand-new account can still be
    // mid-onboarding before any entitlement exists. Check milestones before
    // assuming they belong on pricing_table.
    if (status === null) {
      const milestones = await getOnboardingMilestones(accountId);
      if (milestones && !milestones.channel_connected) {
        return frontendURL(`${accountPath}/onboarding_shell`);
      }
      return frontendURL(`${accountPath}/pricing_table`);
    }
    if (!['active', 'trialing'].includes(status)) {
      return frontendURL(`${accountPath}/pricing_table`);
    }
    if (ssoConversationId) {
      return frontendURL(`${accountPath}/conversations/${ssoConversationId}`);
    }
    const milestones = await getOnboardingMilestones(accountId);
    if (milestones && !milestones.channel_connected) {
      return frontendURL(`${accountPath}/onboarding_shell`);
    }
    return DEFAULT_REDIRECT_URL;
  }
  return DEFAULT_REDIRECT_URL;
};
